import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './button';

const OnboardingTour = ({ 
  steps, 
  isOpen, 
  onComplete, 
  onSkip,
  storageKey = 'onboarding_completed'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState({});
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const updateHighlight = useCallback(() => {
    if (!steps[currentStep]?.target) {
      setHighlightStyle({});
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const element = document.querySelector(steps[currentStep].target);
    if (element) {
      const rect = element.getBoundingClientRect();
      const padding = 8;
      
      setHighlightStyle({
        top: rect.top - padding + window.scrollY,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      // Calculate tooltip position
      const placement = steps[currentStep].placement || 'bottom';
      let tooltipTop, tooltipLeft;
      
      switch (placement) {
        case 'top':
          tooltipTop = rect.top + window.scrollY - 20;
          tooltipLeft = rect.left + rect.width / 2;
          break;
        case 'bottom':
          tooltipTop = rect.bottom + window.scrollY + 20;
          tooltipLeft = rect.left + rect.width / 2;
          break;
        case 'left':
          tooltipTop = rect.top + window.scrollY + rect.height / 2;
          tooltipLeft = rect.left - 20;
          break;
        case 'right':
          tooltipTop = rect.top + window.scrollY + rect.height / 2;
          tooltipLeft = rect.right + 20;
          break;
        default:
          tooltipTop = rect.bottom + window.scrollY + 20;
          tooltipLeft = rect.left + rect.width / 2;
      }

      setTooltipPosition({ 
        top: tooltipTop, 
        left: tooltipLeft,
        placement 
      });

      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep, steps]);

  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to defer the state update
      const timer = setTimeout(() => {
        updateHighlight();
      }, 100);
      window.addEventListener('resize', updateHighlight);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateHighlight);
      };
    }
  }, [isOpen, currentStep, updateHighlight]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(storageKey, 'true');
    setCurrentStep(0);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(storageKey, 'true');
    setCurrentStep(0);
    onSkip?.();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 transition-opacity duration-300" />
      
      {/* Highlight cutout */}
      {highlightStyle.width && (
        <div
          className="absolute bg-transparent rounded-lg ring-4 ring-teal-500 ring-opacity-80 transition-all duration-300 pointer-events-none"
          style={{
            top: highlightStyle.top,
            left: highlightStyle.left,
            width: highlightStyle.width,
            height: highlightStyle.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className={`absolute z-10 w-[380px] max-w-[90vw] transition-all duration-300 ${
          tooltipPosition.placement === 'top' ? 'transform -translate-x-1/2 -translate-y-full' :
          tooltipPosition.placement === 'bottom' ? 'transform -translate-x-1/2' :
          tooltipPosition.placement === 'left' ? 'transform -translate-x-full -translate-y-1/2' :
          tooltipPosition.placement === 'right' ? 'transform -translate-y-1/2' :
          ''
        }`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
        }}
      >
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">{step.title}</span>
              </div>
              <button
                onClick={handleSkip}
                className="text-white/80 hover:text-white transition-colors"
                data-testid="skip-tour-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-4">
            {step.icon && (
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center text-3xl">
                  {step.icon}
                </div>
              </div>
            )}
            <p className="text-gray-600 text-sm leading-relaxed">{step.content}</p>
            
            {step.tips && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 text-xs font-medium">💡 Tip: {step.tips}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              {/* Progress dots */}
              <div className="flex items-center space-x-1.5">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentStep 
                        ? 'bg-teal-600 w-4' 
                        : index < currentStep 
                          ? 'bg-teal-400' 
                          : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center space-x-2">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                    data-testid="prev-step-button"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  data-testid="next-step-button"
                >
                  {isLastStep ? (
                    <>
                      Get Started
                      <Sparkles className="w-4 h-4 ml-1" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Step counter */}
            <p className="text-center text-xs text-gray-400 mt-3">
              Step {currentStep + 1} of {steps.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
